/**
 * Bewertungs-Typen für CherryPulse
 *
 * NEUE STRUKTUR (Februar 2024):
 * - Sterne-System: 0-5 Sterne (statt Schulnoten)
 * - 3 Bewertungsebenen: Agentur, Team, Chemie (je 33%)
 * - Haupt- und Unterkriterien (aufklappbar)
 * - Event-basierte Bewertungen
 * - Zwei Personas: CherryPicker vs. Endkunde
 */

// ============================================================================
// STAR RATING SYSTEM
// ============================================================================

/**
 * Sterne-Bewertung (0-5)
 * 0 = Nicht bewertet / Keine Erfahrung
 * 1 = Mangelhaft
 * 2 = Ausreichend
 * 3 = Befriedigend
 * 4 = Gut
 * 5 = Sehr gut
 */
export type StarRating = 0 | 1 | 2 | 3 | 4 | 5;

/**
 * Labels für Sterne-Bewertungen
 */
export const STAR_LABELS: Record<StarRating, string> = {
  0: 'Nicht bewertbar',
  1: 'Nicht überzeugend',
  2: 'Weniger überzeugend',
  3: 'Durchschnitt',
  4: 'Überzeugend',
  5: 'Sehr überzeugend',
};

// ============================================================================
// USER PERSONAS
// ============================================================================

/**
 * Benutzer-Persona für unterschiedliche Feature-Umfänge
 * - cherrypicker: CherryPicker-Mitarbeiter (ohne NPS)
 * - customer: Endkunden-Mitarbeiter (mit NPS, Zukunft)
 */
export type UserPersona = 'cherrypicker' | 'customer';

// ============================================================================
// EVALUATION LEVELS (3 Ebenen)
// ============================================================================

/**
 * Die drei Bewertungsebenen
 */
export type EvaluationLevel = 'agency' | 'team' | 'chemistry' | 'longlist';

/**
 * Gewichtung der Ebenen (gleichgewichtet)
 */
export const LEVEL_WEIGHTS: Record<EvaluationLevel, number> = {
  agency: 1 / 3,
  team: 1 / 3,
  chemistry: 1 / 3,
  longlist: 1, // Longlist is standalone (not mixed with other levels)
};

// ============================================================================
// AGENCY LEVEL - Eignung & Kompetenzen (33%)
// ============================================================================

/**
 * Hauptkriterien: Agenturebene
 */
export type AgencyMainCriterionId =
  | 'leadAgencySuitability' // Agentur-Typ (umbenannt)
  | 'strategicCompetence' // Strategische Kompetenz (promoted von Sub)
  | 'workQuality' // Qualität der Arbeitsergebnisse (promoted von Sub)
  | 'marketUnderstanding' // Branchen- und Marktverständnis
  | 'internationalExperience' // Internationale Erfahrung (optional)
  | 'taskFit'; // Passung zur Aufgabenstellung

/**
 * Scores für Agenturebene
 */
export interface AgencyLevelScores {
  main: Partial<Record<AgencyMainCriterionId, StarRating>>;
}

// ============================================================================
// TEAM LEVEL - Fachliche Stärke des Kernteams (33%)
// ============================================================================

/**
 * Hauptkriterien: Teamebene (flat - alle auf einer Ebene)
 */
export type TeamMainCriterionId =
  | 'strategyCompetence' // Kompetenz Strategie
  | 'creativeCompetence' // Kompetenz Kreation
  | 'targetedCreativity' // Zielgerichtete Kreativität (promoted von Sub)
  | 'accountCompetence' // Kompetenz Beratung/Account
  | 'consultingCompetence' // Beratungskompetenz (promoted von Sub)
  | 'teamDynamics' // Rollenverteilung & Teamzusammenspiel
  | 'projectManagement' // Projektmanagement (promoted von Sub)
  | 'discussionQuality' // Qualität der Diskussionen & Lösungsansätze
  | 'proactivity'; // Proaktivität (konsolidiert aus proactivityStrength + proactivity)

/**
 * Scores für Teamebene
 */
export interface TeamLevelScores {
  main: Partial<Record<TeamMainCriterionId, StarRating>>;
}

// ============================================================================
// CHEMISTRY LEVEL - Kulturelle Passung (33%)
// ============================================================================

/**
 * Hauptkriterien: Chemie
 */
export type ChemistryMainCriterionId =
  | 'teamFit' // Teamfit & kulturelle Passung
  | 'passionEnthusiasm' // Herzblut & Begeisterung
  | 'communicationStyle' // Kommunikationsstil & Diskussionsfähigkeit
  | 'chemistry' // Chemie (promoted von Sub)
  | 'reliability' // Verlässlichkeit & Termintreue (konsolidiert)
  | 'feedbackOpenness'; // Offenheit für Feedback

/**
 * Scores für Chemie-Ebene
 */
export interface ChemistryLevelScores {
  main: Partial<Record<ChemistryMainCriterionId, StarRating>>;
}

// ============================================================================
// LONGLIST LEVEL - Eigenes Schema für Longlist Workshop
// ============================================================================

/**
 * Hauptkriterien: Longlist Workshop (7 Kriterien, 0-5)
 */
export type LonglistCriterionId =
  | 'longlistStrategy' // Strategische Kompetenz
  | 'longlistCreativity' // Kreative Exzellenz
  | 'longlistDigital' // Ganzheitliches & digitales Denken
  | 'longlistMarket' // Branchen- & Marktverständnis
  | 'longlistCaseFit' // Referenzen & Case-Fit
  | 'longlistTeam' // Team & Organisation
  | 'longlistMotivation'; // Motivation & Gesamtüberzeugung

/**
 * Scores für Longlist-Bewertung
 */
export interface LonglistLevelScores {
  main: Partial<Record<LonglistCriterionId, StarRating>>;
}

// ============================================================================
// COMBINED RATING SCORES
// ============================================================================

/**
 * Alle Hauptkriterien-IDs
 */
export type MainCriterionId = AgencyMainCriterionId | TeamMainCriterionId | ChemistryMainCriterionId | LonglistCriterionId;

/**
 * Alle Kriterien-IDs
 */
export type CriterionId = string;

/**
 * Vollständige Score-Struktur für eine Bewertung
 */
export interface RatingScores {
  agency: AgencyLevelScores;
  team: TeamLevelScores;
  chemistry: ChemistryLevelScores;

  /**
   * Longlist-Workshop Bewertung (7 eigene Kriterien)
   * Wird nur bei occasion === 'longlist_workshop' verwendet
   */
  longlist?: LonglistLevelScores;


}

// ============================================================================
// TEAM MEMBER EVALUATION (Personenbewertung)
// ============================================================================

// ----------------------------------------------------------------------------
// ROADMAP-RESERVED TYPES (Phase 5 - Personenbewertung)
// ----------------------------------------------------------------------------
// The following types (TeamRole, TEAM_ROLE_LABELS, TeamMemberEvaluation) are
// reserved for the upcoming Personenbewertung feature (roadmap phase 5).
// They are intentionally kept exported even though they are not yet wired up
// in any component. Do not remove without updating docs/ROADMAP.md.
// ----------------------------------------------------------------------------

/**
 * Vordefinierte Rollen für Teammitglieder
 */
export type TeamRole = 'strategist' | 'creative' | 'accountManager' | 'custom';

/**
 * Labels für Team-Rollen
 */
export const TEAM_ROLE_LABELS: Record<TeamRole, string> = {
  strategist: 'Stratege/in',
  creative: 'Kreative/r',
  accountManager: 'Account Manager/in',
  custom: 'Andere Rolle',
};

/**
 * Bewertung eines einzelnen Teammitglieds
 */
export interface TeamMemberEvaluation {
  id: string;
  role: TeamRole;
  customRoleName?: string; // Wenn role === 'custom'
  name?: string; // Optional: Name der Person
  scores: Partial<Record<TeamMainCriterionId, StarRating>>; // Teilmenge der Team-Kriterien
}

// ============================================================================
// EVALUATION EVENTS (Bewertungsanlässe)
// ============================================================================
// NOTE: EvaluationEvent (below) is reserved for the roadmap-planned Event
// Management System (roadmap phase 5). The DB schema is ready, but no UI/API
// is wired up yet. Do not remove without updating docs/ROADMAP.md.

/**
 * Event-Typen für Bewertungsanlässe
 */
export type EvaluationEventType =
  | 'longlist_workshop' // Longlist Workshop (8-10 Agenturen)
  | 'chemistry_meeting' // Chemistry Meeting (4-5 Agenturen)
  | 'pitch' // Engagement Workshop / Pitch (2-3 Agenturen)
  | 'final_presentation' // Abschlusspräsentation (2-3 Agenturen)
  | 'other'; // Sonstiges

/**
 * Labels und Metadaten für Event-Typen
 */
export const EVALUATION_EVENT_TYPES: {
  value: EvaluationEventType;
  label: string;
  icon: string;
  typicalAgencyCount: string;
}[] = [
  {
    value: 'longlist_workshop',
    label: 'Longlist Workshop',
    icon: '📝',
    typicalAgencyCount: '8-10',
  },
  { value: 'chemistry_meeting', label: 'Chemistry Meeting', icon: '🤝', typicalAgencyCount: '4-5' },
  {
    value: 'pitch',
    label: 'Engagement Workshop / Pitch',
    icon: '🎤',
    typicalAgencyCount: '2-3',
  },
  {
    value: 'final_presentation',
    label: 'Abschlusspräsentation',
    icon: '🏆',
    typicalAgencyCount: '2-3',
  },
  { value: 'other', label: 'Sonstiges', icon: '📝', typicalAgencyCount: '1+' },
];

/**
 * Evaluation Event - Bewertungsanlass mit mehreren Agenturen
 */
export interface EvaluationEvent {
  id: string;
  partitionKey: string; // Für Cosmos DB

  type: EvaluationEventType;
  name?: string; // Optional: "Pitch Deutsche Bank Q1"
  date: string; // ISO 8601
  agencyIds: string[]; // Zugeordnete Agenturen

  // Metadaten
  createdBy: string;
  createdAt: string;
  updatedAt?: string;
}

// ============================================================================
// RATING NOTES
// ============================================================================

/**
 * Optionale Notizen - für alle Kriterien
 */
export interface RatingNotes {
  // Agenturebene
  agency?: {
    main?: Partial<Record<AgencyMainCriterionId, string>>;
  };

  // Teamebene
  team?: {
    main?: Partial<Record<TeamMainCriterionId, string>>;
  };

  // Chemie
  chemistry?: {
    main?: Partial<Record<ChemistryMainCriterionId, string>>;
  };

  // Longlist
  longlist?: {
    main?: Partial<Record<LonglistCriterionId, string>>;
  };

  // Gesamtnotizen
  overall?: string;
}

// ============================================================================
// COMMON TYPES
// ============================================================================

export type Sentiment = 'positive' | 'neutral' | 'negative';

export type RatingSource = 'manual' | 'import' | 'voice' | 'voice_realtime';

/**
 * Status einer Bewertung
 */
export type RatingStatus = 'draft' | 'completed';
// 'voice' und 'voice_realtime' werden in der UI deaktiviert, bleiben aber für Legacy-Daten

/**
 * KI-generierte Insights
 */
export interface AIInsights {
  sentiment: Sentiment;
  keywords: string[];
  summary: string;
  confidence: number; // 0-1
}

/**
 * Optionaler Projekt-Kontext.
 *
 * Roadmap-reserved: currently attached to Rating but no UI captures these
 * fields yet. Kept for the planned Event Management / Personenbewertung flows
 * (roadmap phase 5). Do not remove without updating docs/ROADMAP.md.
 */
export interface ProjectContext {
  projectName?: string;
  projectType?: string;
  budget?: string;
  duration?: string;
}

/**
 * Bewertenden-Info (minimale User-Daten)
 */
export interface RatedByInfo {
  id: string;
  displayName: string;
  avatar?: string;
}

// ============================================================================
// RATING (Vollständige Bewertung)
// ============================================================================

/**
 * Vollständige Bewertung - NEUE STRUKTUR
 */
export interface Rating {
  // Cosmos DB Pflichtfelder
  id: string;
  partitionKey: string; // = agencyId

  // Referenzen
  agencyId: string;
  userId: string;
  ratedBy?: RatedByInfo;

  // NEU: Event-Referenz für Vergleichbarkeit
  eventId?: string;

  // Kunde, für den die Bewertung erstellt wird
  customerName?: string;

  // NEU: Persona des Bewertenden (optional für Legacy-Kompatibilität)
  persona?: UserPersona;

  // Quantitative Bewertung - 3-Ebenen-Struktur
  scores: RatingScores;

  // Qualitative Bewertung
  notes?: RatingNotes;

  // Optional: Personenbewertungen
  teamMembers?: TeamMemberEvaluation[];

  // KI-generierte Insights
  aiInsights?: AIInsights;

  // Kontext
  projectContext?: ProjectContext;

  // Status (draft = Zwischengespeichert, completed = Abgeschlossen)
  status?: RatingStatus;

  // Metadaten
  createdAt: string; // ISO 8601
  period: string; // Format: "YYYY-MM"
  source: RatingSource;

  // Letzte Bearbeitung (zusätzlich zum vollständigen editHistory)
  editedAt?: string | null;
  editReason?: string | null;

  // Vollständiger Audit-Trail aller Bearbeitungen (ältester zuerst)
  editHistory?: RatingEdit[];

  // LEGACY: Für Abwärtskompatibilität (wird in Phase 8 migriert)
  overallNotes?: string;
  occasion?: RatingOccasion;
}

/**
 * Eintrag im Audit-Log einer Bewertung: wer hat wann warum geändert.
 */
export interface RatingEdit {
  id: string;
  editedByUserId: string;
  editedByName: string | null;
  editedByAvatar: string | null;
  editReason: string;
  editedAt: string;
}

/**
 * Für neue Bewertungen (ohne id und Metadaten)
 */
export type CreateRatingInput = Omit<Rating, 'id' | 'partitionKey' | 'createdAt' | 'period' | 'aiInsights'>;

/**
 * Für den Rating-Wizard: Aktueller Zustand.
 *
 * Roadmap-reserved: the current wizard stores draft state directly in the
 * Zustand store rather than passing a RatingDraft object. This type is kept
 * for the planned Event Management / Personenbewertung flows (roadmap phase
 * 5). Do not remove without updating docs/ROADMAP.md.
 */
export interface RatingDraft {
  agencyId: string;
  eventId?: string;
  scores: Partial<RatingScores>;
  notes: Partial<RatingNotes>;
  teamMembers?: TeamMemberEvaluation[];
  projectContext?: ProjectContext;
}

// ============================================================================
// RATING OCCASION (vereinfacht)
// ============================================================================

/**
 * Bewertungsanlass - kombiniert Event-Typen und manuelle Anlässe
 */
export type RatingOccasion = EvaluationEventType;

export const RATING_OCCASIONS: { value: RatingOccasion; label: string; icon: string }[] =
  EVALUATION_EVENT_TYPES.map((e) => ({ value: e.value, label: e.label, icon: e.icon }));
